import { Inject, Injectable, PLATFORM_ID, TransferState } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ParamMap, Params } from '@angular/router';
import { Title } from '@angular/platform-browser';

import {
  ConverterService, LanguageService, OccEndpointsService, PRODUCT_SEARCH_PAGE_NORMALIZER, ProductSearchPage
} from '@spartacus/core';

import {
  BehaviorSubject, forkJoin, Observable, of, throwError,
  catchError, first, map, switchMap, take, tap
} from 'rxjs';

import { IIngredient, IRecipeStep } from '@seb/web-design-system/recipe-overview';
import { IRecipeUser, RecipeUserEnum } from '@seb/web-design-system/recipe-user';
import { IMedia, LinkUtils, PositionEnum, ThemesEnum } from '@seb/web-design-system/core';
import { IRecipeCard } from '@seb/web-design-system/recipe-card';
import { IDevice } from '@seb/web-design-system/devices';
import { IReview } from '@seb/web-design-system/reviews';

import { RecipeMediaTypeEnum, RecipeProgramParameterEnum } from '../enums';
import { WebCommonConfig } from '../config/configs/web-common.config';
import { RECIPES_KEY, FIRST_RECIPE_VARIANT_KEY } from '../constants';
import { ConfigurationService } from './configuration.service';
import { UrlMappingService } from './url-mapping.service';
import { BaseStoreService } from './base-store.service';
import { HobStateService } from './hob-state.service';
import { ImageService } from './image.service';
import { CustomMedia } from '../models';

import {
  RecipeMedia, RecipeOperation, RecipeParameter, RecipeSearchItem, RecipeSearchParamsUrl, RecipeStep,
  Recipe, RecipeComment, ApplianceRange, DcpListResponse, RecipeIngredient, RecipeKitchenware,
  DcpRecipeSearchRequestBodyFacetFilter, DcpRecipeSearchRequestBodyFieldFilter
} from '../models/recipe.model';

const domainIconMap = {
  PRO_COO: 'icon-cookeo',
  PRO_COM: 'icon-companion',
  PRO_CAK: 'icon-cakefactory',
  PRO_BLE: 'icon-blender',
  PRO_ACT: 'icon-actifry',
  PRO_OPG: 'icon-optigrill',
  PRO_COP: 'icon-cookingconnect',
  PRO_STE: 'icon-steamup'
};

@Injectable({
  providedIn: 'root'
})
export class RecipeService {
  private topRecipes: { [code: string]: BehaviorSubject<Recipe> } = {};
  private recipes: { [code: string]: BehaviorSubject<Recipe> } = {};

  private get config(): WebCommonConfig {
    return this.configService.config;
  }

  constructor(
    private configService: ConfigurationService,
    private http: HttpClient,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private occEndpointsService: OccEndpointsService,
    private baseStoreService: BaseStoreService,
    private imageService: ImageService,
    private converterService: ConverterService,
    private urlMappingService: UrlMappingService,
    private transferState: TransferState,
    private titleService: Title,
    private readonly hobStateService: HobStateService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  formatRecipeDuration(duration: number): string {
    if (duration >= 60) {
      const hours: number = Math.floor(duration / 60);
      const minutes: number = duration % 60;
      if (!minutes) {
        // 0 minutes case
        return this.translateService.instant('recipes.blockTimer.hours', {
          duration: hours
        });
      }
      return `${this.translateService.instant('recipes.blockTimer.hours', {
        duration: hours
      })} ${this.translateService.instant('recipes.blockTimer.minutes', {
        duration: minutes
      })}`;
    }

    return this.translateService.instant('recipes.blockTimer.minutes', {
      duration
    });
  }

  getRecipe(recipeSourceSystem: string, recipeId: string): Observable<Recipe> {
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(RECIPES_KEY)) {
      const recipe = this.transferState
        .get(RECIPES_KEY, [])
        .find((r) => r.fid.functionalId === recipeId && r.fid.sourceSystem === recipeSourceSystem);

      if (recipe !== undefined) {
        return of(recipe);
      }
    }

    if (!this.recipes[`${recipeSourceSystem}_${recipeId}`]) {
      this.recipes[`${recipeSourceSystem}_${recipeId}`] = new BehaviorSubject(undefined);
      this.loadRecipe(recipeSourceSystem, recipeId);
    }

    return this.recipes[`${recipeSourceSystem}_${recipeId}`].pipe(first((recipe) => recipe !== undefined));
  }

  private loadRecipe(recipeSourceSystem: string, recipeId: string): void {
    const recipeUrl = `${this.config.dcp?.baseUrl}/v3/recipes/${recipeSourceSystem}/${encodeURIComponent(
      recipeId
    )}?format=mobile`;
    const headers = {
      ApiKey: this.config.dcp?.apiKey,
      'Content-Type': 'application/json' // Force header on GET => implementation error on DCP API
    };

    this.http.get<Recipe>(recipeUrl, { headers }).pipe(
      tap((recipe) => {
        if (isPlatformServer(this.platformId)) {
          const stateRecipes = this.transferState.get(RECIPES_KEY, []);
          stateRecipes.push(recipe);
          this.transferState.set(RECIPES_KEY, stateRecipes);
        }
      })
    )
      .subscribe({
        next: (recipe) => this.recipes[`${recipeSourceSystem}_${recipeId}`].next(recipe),
        error: (error) => this.recipes[`${recipeSourceSystem}_${recipeId}`].error(error)
      });
  }

  private loadTopRecipe(topRecipeId: string): void {
    const topRecipeUrl = `${this.config.dcp?.baseUrl}/v3/topRecipes/${encodeURIComponent(
      topRecipeId
    )}?fields=variants,title,isReworked`;
    const headers = {
      ApiKey: this.config.dcp?.apiKey,
      'Content-Type': 'application/json' // Force header on GET => implementation error on DCP API
    };

    this.http.get<Recipe>(topRecipeUrl, { headers })
      .subscribe({
        next: (recipe) => this.topRecipes[topRecipeId].next(recipe),
        error: (error) => this.topRecipes[topRecipeId].error(error)
      });
  }

  private getRecipeVariants(topRecipeId: string): Observable<Recipe> {
    if (!this.topRecipes[topRecipeId]) {
      this.topRecipes[topRecipeId] = new BehaviorSubject(undefined);
      this.loadTopRecipe(topRecipeId);
    }
    return this.topRecipes[topRecipeId].pipe(first((recipe) => recipe !== undefined));
  }

  getFirstRecipeVariant(recipeSourceSystem: string, topRecipeId: string): Observable<Recipe> {
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(FIRST_RECIPE_VARIANT_KEY)) {
      const recipe = this.transferState.get(FIRST_RECIPE_VARIANT_KEY, undefined);
      if (recipe !== undefined) {
        this.transferState.remove(FIRST_RECIPE_VARIANT_KEY);
        return of(recipe);
      }
    }
    return this.getRecipeVariants(topRecipeId).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 410) {
          return throwError(() => error);
        }
        return this.getRecipeVariants(`/v1/recipes/${topRecipeId}/`);
      }),
      switchMap((result) => {
        const firstRecipeId = result.variants?.[0]?.functionalId;
        return this.getRecipe(recipeSourceSystem, firstRecipeId);
      }),
      tap((recipe) => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(FIRST_RECIPE_VARIANT_KEY, recipe);
        }
      })
    );
  }

  getRecipeComments(
    recipeSourceSystem: string,
    recipeId: string,
    page = 0,
    size = 3
  ): Observable<DcpListResponse<RecipeComment>> {
    const recipeCommentsUrl = `${this.config.dcp?.baseUrl}/v3/recipes/${recipeSourceSystem}/${encodeURIComponent(
      recipeId
    )}/comments?page=${page}&size=${size}`;
    const headers = {
      ApiKey: this.config.dcp?.apiKey,
      'Content-Type': 'application/json' // Force header on GET => implementation error on DCP API
    };

    return this.http.get<DcpListResponse<RecipeComment>>(recipeCommentsUrl, { headers });
  }

  searchForCover(resourceMedias: RecipeMedia[] | undefined): RecipeMedia | undefined {
    return resourceMedias?.find((resourceMedia: RecipeMedia) =>
      resourceMedia.isCover && resourceMedia.media.type === RecipeMediaTypeEnum.PHOTO
    ) || resourceMedias?.find(
      (resourceMedia: RecipeMedia) => resourceMedia.media.type === RecipeMediaTypeEnum.PHOTO
    );
  }

  extractYields(recipe: Recipe): number[] {
    const yields: number[] = [];
    if (recipe.yield) {
      yields.push(recipe.yield.quantity);
    }
    recipe.variants?.forEach((variant: Recipe) => {
      if (variant.yield) {
        yields.push(variant.yield.quantity);
      }
    });
    return yields.sort((a, b) => a - b);
  }

  extractIngredient(ingredient: RecipeIngredient): IIngredient {
    return {
      quantity: ingredient.quantity,
      unit: ingredient.unit
        ? {
          name: ingredient.unit?.name,
          abbreviation: ingredient.unit?.abbreviation,
          pluralName: ingredient.unit?.pluralName
        }
        : null,
      name: ingredient.applianceDescription
    };
  }

  extractKitchenware(kitchenware: RecipeKitchenware): IDevice {
    const kitchenwareMedia = this.searchForCover(kitchenware.resourceMedias);
    return {
      label: kitchenware.name,
      ...(kitchenwareMedia && {
        media: this.imageService.normalizeMedia({
          altText: kitchenware.name,
          url: kitchenwareMedia.media.thumbnail
        } as CustomMedia)
      })
    };
  }

  extractStep(step: RecipeStep): IRecipeStep {
    const iRecipeStep = {
      description: step.applicationDescription,
      ingredients: step.ingredients?.map(this.extractIngredient) || [],
      kitchenwares:
        step.kitchenwares?.map((kitchenware: RecipeKitchenware) => ({
          icon: 'icon-kitchenware',
          text: kitchenware.name
        })) || [],
      parameters: []
    };

    iRecipeStep.parameters = [];

    if (step.sequences?.length && step.sequences[0].operations?.length) {
      const operation: RecipeOperation = step.sequences[0].operations[0];

      if (operation.program?.name) {
        iRecipeStep.parameters.push({
          icon: 'icon-program-type',
          text: operation.program.name
        });
      }

      if (operation.parameters?.length) {
        operation.parameters
          .filter((param: RecipeParameter) => Object.keys(RecipeProgramParameterEnum).includes(param.commonKey))
          .forEach((param: RecipeParameter) => {
            let parameterLabel = '-';
            let icon = 'icon-program-type';

            switch (param.commonKey) {
              case RecipeProgramParameterEnum.DURATION:
                icon = 'icon-program-duration';
                if (param.value) {
                  let duration = parseInt(param.value, 10);
                  if (!isNaN(duration)) {
                    let durationUnit = 'seconds';
                    if (duration >= 60) {
                      durationUnit = 'minutes';
                      duration = Math.ceil(duration / 60);
                    }
                    parameterLabel = this.translateService.instant(
                      `recipes.blockPreparation.stepDuration.${durationUnit}`,
                      { duration }
                    );
                  }
                }
                break;
              case RecipeProgramParameterEnum.SPEED:
                icon = 'icon-program-speed';
                parameterLabel = param.value ?? parameterLabel;
                break;
              case RecipeProgramParameterEnum.TEMPERATURE:
                parameterLabel = `${param.value ?? parameterLabel} °C`;
                icon = 'icon-program-temperature';
                break;
              default:
                parameterLabel = param.value ?? parameterLabel;
                break;
            }

            iRecipeStep.parameters.push({
              icon,
              text: parameterLabel
            });
          });
      }
    }

    return iRecipeStep;
  }

  extractReview(recipeTitle: string, comment: RecipeComment): IReview {
    const commentDate = new Date(comment.creationDate);
    return {
      recipeTitle,
      author: comment.creator?.nickname,
      creationDate: this.translateService.instant('recipes.comments.creationDate', {
        day: ('0' + commentDate.getDate()).slice(-2),
        month: ('0' + (commentDate.getMonth() + 1)).slice(-2),
        year: commentDate.getFullYear().toString().substr(-2)
      }),
      rating: comment.evaluation?.rating ? {
        averageRatings: comment.evaluation?.rating,
        numberOfReviews: 1
      } : undefined,
      content: comment.content
    };
  }

  queryParamsToRecipeSearchParams(queryParams: ParamMap): RecipeSearchParamsUrl {
    const recipeSearchParams: RecipeSearchParamsUrl = {};

    if (queryParams.has('sort')) {
      const splitSort = queryParams.get('sort').split(':');
      recipeSearchParams.sort = { name: splitSort[0], direction: splitSort[1] };
    }

    if (queryParams.has('filters')) {
      recipeSearchParams.filters = [];
      queryParams.getAll('filters').forEach((filter) => {
        const splitFilter = filter.split(':');
        const recipeFilter: DcpRecipeSearchRequestBodyFieldFilter = {
          field: splitFilter[0],
          values: splitFilter[1].split(',')
        };
        if (splitFilter.length === 3) {
          recipeFilter.match = splitFilter[2];
        }
        recipeSearchParams.filters.push(recipeFilter);
      });
    }

    if (queryParams.has('facets')) {
      recipeSearchParams.facets = [];
      queryParams.getAll('facets').forEach((facet) => {
        const splitFacet = facet.split(':');
        const recipeFacet: DcpRecipeSearchRequestBodyFacetFilter = {
          facet: splitFacet[0],
          key: splitFacet[1],
          type: 'inclusion'
        };
        recipeSearchParams.facets.push(recipeFacet);
      });
    }

    return recipeSearchParams;
  }

  recipeSearchParamsToQueryParams(recipeSearchParams: RecipeSearchParamsUrl): Params {
    const queryParams: Params = {};
    queryParams['sort'] = `${recipeSearchParams.sort.name}:${recipeSearchParams.sort.direction}`;

    if (recipeSearchParams.filters?.length > 0) {
      queryParams['filters'] = [];
      recipeSearchParams.filters.forEach((filter) => {
        let queryFilter = `${filter.field}:${filter.values.join(',')}`;
        if (filter.match) {
          queryFilter = `${queryFilter}:${filter.match}`;
        }
        queryParams['filters'].push(queryFilter);
      });
    }

    if (recipeSearchParams.facets?.length > 0) {
      queryParams['facets'] = recipeSearchParams.facets.map((facet) => `${facet.facet}:${facet.key}`);
    }

    return queryParams;
  }

  search(
    q = '',
    page = 0,
    size = 9,
    recipeSearchParams: RecipeSearchParamsUrl = {},
    productCode?: string,
    recipeIdToBeExcluded?: string
  ): Observable<DcpListResponse<RecipeSearchItem>> {
    const baseStore = this.baseStoreService.baseStore;

    return this.languageService.getActive().pipe(
      take(1),
      switchMap((lang: string) => {
        const bodyProductSearch = {
          productFilters: [{ cmmfCodes: [productCode] }]
        };

        const fieldFilterRecipeId = recipeIdToBeExcluded ? {
          filters: [{
            field: 'topRecipe.id.functionalId',
            values: [recipeIdToBeExcluded],
            type: 'exclusion'
          }]
        } : {
          filters: []
        };

        const fieldFilterBrand = {
          field: 'brand.key',
          values: this.hobStateService.getActiveBrands() ?? [baseStore.brand_SF]
        };

        const checkedMarket: string = baseStore.market === 'AT' ? 'DE' : baseStore.market;

        const bodyRecipeListSearch = {
          fieldList: [
            'title',
            'id',
            'seoUrl',
            'durations.totalTime',
            'domain',
            'creationDate',
            'modificationDate',
            'lang',
            'status',
            'market',
            'creator',
            'resourceMedias',
            'rating',
            'brand',
            'topRecipe.type',
            'topRecipe.id',
            'mobile.partner',
            'isReworked'
          ],
          facetList: [
            {
              name: 'domain'
            },
            // Temporary remove 'applianceGroups' facet until DCP fix 'appliance' facet
            // {
            //   name: 'applianceGroups'
            // },
            {
              name: 'courses'
            },
            {
              name: 'occasions'
            }
            // {
            //   name: 'authorRecipe'
            // }
          ],
          facetFilters: [
            {
              facet: 'withPhoto',
              key: 'true',
              type: 'inclusion'
            },
            ...(recipeSearchParams.facets?.length ? recipeSearchParams.facets : [])
          ],
          fieldFilters: [
            {
              field: 'lang.key',
              values: [lang.substring(0, 2)]
            },
            {
              field: 'market.key',
              values: [`GS_${checkedMarket}`]
            },
            fieldFilterBrand,
            {
              field: 'classifications.key_FOOD_COOKING',
              values: ['IS_FOOD_COOKING'],
              type: 'exclusion'
            },
            {
              field: 'topRecipe.type.key',
              values: ['TEMPLATE'],
              type: 'exclusion'
            },
            {
              field: 'status.key',
              values: productCode ? ['PUBLISHED'] : ['PUBLISHED', 'VALIDATED_MARKET']
            },
            ...(fieldFilterRecipeId.filters?.length ? fieldFilterRecipeId.filters : []),
            ...(recipeSearchParams.filters?.length ? recipeSearchParams.filters : [])
          ],
          ...(recipeSearchParams.sort && {
            sort: recipeSearchParams.sort
          })
        };

        const recipeSearchUrl = `${this.config.dcp?.baseUrl}/v4/search/recipes?groupBy=topRecipe&q=${encodeURIComponent(
          q
        )}&page=${page}&size=${size}&market=GS_${checkedMarket}&lang=${lang.substring(0, 2)}`;
        const headers = {
          ApiKey: this.config.dcp?.apiKey,
          'Content-Type': 'application/json'
        };
        const body = productCode ? { ...bodyRecipeListSearch, ...bodyProductSearch } : { ...bodyRecipeListSearch };
        return this.http.post<DcpListResponse<RecipeSearchItem>>(recipeSearchUrl, body, { headers });
      })
    );
  }

  extractRecipeSearchItem(item: RecipeSearchItem): Observable<IRecipeCard> {
    if (!item) return of(null);
    const recipeType = item.partnerDetails ? RecipeUserEnum.CHEF : item.recipeType || RecipeUserEnum.BRAND;
    const userText: string = item.partnerDetails?.name || (item.recipeType === RecipeUserEnum.UGC ? item.creator || '-' : item.brand?.name);

    return this.urlMappingService.getUrlMappingByCode('/recipe').pipe(
      take(1),
      map((result) => {
        let recipeTitle = item.title?.replaceAll(/\./g, '').trim();
        recipeTitle = recipeTitle?.charAt(0).toUpperCase() + recipeTitle?.slice(1);
        const baseUrl = item.seoUrl || `/${item.identifier?.sourceSystem}/${recipeTitle}/${item.topRecipeId}`;
        return {
          sourceSystem: item.identifier.sourceSystem,
          id: item.identifier.functionalId,
          title: recipeTitle,
          link: {
            url: `${result.value || '/recipe'}/detail${LinkUtils.resolveUrlLink(baseUrl)}`,
            linkName: item.title
          },
          topRecipeId: item.topRecipeId,
          user: {
            type: recipeType,
            image: {
              url: item.partnerDetails?.media?.thumbnail || item.brand?.thumbnailUrl,
              altText: userText
            },
            text: userText
          },
          rating: item.community?.rating,
          countReviews: item.community?.comments,
          timeLabel: this.formatRecipeDuration(item.durations?.totalTime),
          deviceIcon: domainIconMap[item.domain],
          media: this.extractCover(item),
          tags: item.isReworked
            ? [
              {
                text: this.translateService.instant('recipes.tag.recipeImproved.label'),
                theme: ThemesEnum.promo2,
                iconPos: PositionEnum.left,
                media: {
                  altText: this.translateService.instant('recipes.tag.recipeImproved.label'),
                  url: this.translateService.instant('recipes.tag.recipeImproved.icon'),
                  mime: 'image'
                }
              }
            ]
            : []
        };
      })
    );
  }

  extractRecipe(recipe: Recipe): Observable<IRecipeCard> {
    if (!recipe) return of(null);
    return this.urlMappingService.getUrlMappingByCode('/recipe').pipe(
      take(1),
      map((result) => {
        let recipeTitle = recipe.title.replaceAll(/\./g, '').trim();
        recipeTitle = recipeTitle.charAt(0).toUpperCase() + recipeTitle.slice(1);
        const baseUrl = recipe.seoUrl || `/${recipe.fid.sourceSystem}/${recipeTitle}/${recipe.topRecipeId}`;
        return {
          sourceSystem: recipe.fid.sourceSystem,
          id: recipe.fid.functionalId,
          link: {
            url: `${result.value || '/recipe'}/detail${LinkUtils.resolveUrlLink(baseUrl)}`,
            linkName: recipe.title
          },
          title: recipeTitle,
          topRecipeId: recipe.topRecipeId,
          user: this.extractUser(recipe),
          rating: recipe.communityTopRecipe?.rating,
          countReviews: recipe.communityTopRecipe?.comments,
          timeLabel: this.formatRecipeDuration(recipe.durations?.totalTime),
          deviceIcon: domainIconMap[recipe.domain['key']],
          media: this.extractCover(recipe),
          tags: recipe.isReworked
            ? [
              {
                text: this.translateService.instant('recipes.tag.recipeImproved.label'),
                theme: ThemesEnum.promo2,
                iconPos: PositionEnum.left,
                media: {
                  altText: this.translateService.instant('recipes.tag.recipeImproved.label'),
                  url: this.translateService.instant('recipes.tag.recipeImproved.icon'),
                  mime: 'image'
                }
              }
            ]
            : []
        };
      })
    );
  }

  extractUser(recipe: Recipe): IRecipeUser {
    const recipeType = recipe?.partnerMobile ? RecipeUserEnum.CHEF : recipe?.recipeType?.key || RecipeUserEnum.BRAND;
    const userText: string =
      recipe?.partnerMobile?.name ||
      (recipe?.recipeType?.key === RecipeUserEnum.UGC ? recipe?.creator?.nickname || '-' : recipe?.brand?.name);

    return {
      type: recipeType,
      image: {
        url: this.searchForCover(recipe?.partnerMobile?.resourceMedias || recipe?.brand?.resourceMedias)?.media?.thumbnail,
        altText: userText
      },
      text: userText
    };
  }

  extractCover(recipe: Recipe | RecipeSearchItem): IMedia | undefined {
    const coverMedia: RecipeMedia | undefined = this.searchForCover(recipe.resourceMedias);
    return coverMedia
      ? this.imageService.normalizeMedia({
        url: coverMedia.media.original.replace('original/', '') + '?w=1920&fit=scale',
        altText: this.titleService.getTitle()
      } as CustomMedia)
      : undefined;
  }

  findVariantFromYield(recipe: Recipe, yieldValue: number): Recipe | undefined {
    return recipe.variants?.find((variant: Recipe) => variant.yield?.quantity === yieldValue);
  }

  searchProductsByRangeAndModels(ranges: ApplianceRange[]): Observable<ProductSearchPage> {
    const cleanedRanges: ApplianceRange[] = [];

    ranges
      .filter((range) => !!range?.key)
      .forEach((range) => {
        const existingIndex = cleanedRanges.findIndex((uniqueRange) => uniqueRange.key === range.key);
        if (existingIndex > -1) {
          if (range.models?.length > 0) {
            cleanedRanges[existingIndex].models.push(...range.models);
          }
        } else {
          cleanedRanges.push({ key: range.key, models: range.models ?? [] });
        }
      });

    const modelsList = cleanedRanges.filter((range) => range.models.length > 0).map((range) => range.models);
    const modelsToQuery = modelsList?.length > 0 ? modelsList.reduce((accModels, models) => [...accModels, ...models]) : [];
    const rangesToQuery = cleanedRanges.filter((range) => range.models.length === 0).map((range) => range.key);
    const requests: Observable<ProductSearchPage>[] = [];

    if (modelsToQuery.length > 0) {
      const modelsQuery = ':relevance:model:' + modelsToQuery.join(':model:');
      const modelsUrl = this.occEndpointsService.buildUrl('recipeRangeSearch', { queryParams: { query: modelsQuery } });
      requests.push(this.http.get<ProductSearchPage>(modelsUrl));
    }

    if (rangesToQuery.length > 0) {
      const rangesQuery = ':relevance:range:' + rangesToQuery.join(':range:');
      const rangesUrl = this.occEndpointsService.buildUrl('recipeRangeSearch', { queryParams: { query: rangesQuery } });
      requests.push(this.http.get<ProductSearchPage>(rangesUrl));
    }

    if (requests.length > 0) {
      return forkJoin(requests).pipe(
        catchError(() => of(null)),
        map((results: ProductSearchPage[]) => {
          const modelsAndRangesResult: ProductSearchPage = {
            pagination: { totalResults: 0 },
            products: []
          };
          results.forEach((result: ProductSearchPage) => {
            modelsAndRangesResult.pagination.totalResults += result.pagination.totalResults;
            modelsAndRangesResult.products.push(...result.products);
          });
          return modelsAndRangesResult;
        }),
        this.converterService.pipeable(PRODUCT_SEARCH_PAGE_NORMALIZER)
      );
    } else {
      return of(null);
    }
  }
}
